import React, { useState, useEffect, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import { registerPasskey, listPasskeys, deletePasskey, isWebAuthnSupported, PasskeyCredential } from '../api/webauthn';
import { Banner, Button, Card, IconButton, Section } from './ui';

function getDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Gerät';
}

export const PasskeyManager: React.FC = () => {
  const [credentials, setCredentials] = useState<PasskeyCredential[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const supported = isWebAuthnSupported();

  const loadCredentials = useCallback(async () => {
    try {
      const creds = await listPasskeys();
      setCredentials(creds);
    } catch {
      // Silently fail on load — user might not have any
    }
  }, []);

  useEffect(() => {
    if (supported) loadCredentials();
  }, [supported, loadCredentials]);

  const handleRegister = async () => {
    setError('');
    setSuccess('');
    setIsRegistering(true);
    try {
      const name = `${getDeviceName()} — ${new Date().toLocaleDateString('de-DE')}`;
      await registerPasskey(name);
      setSuccess('Passkey registriert.');
      await loadCredentials();
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError('Registrierung abgebrochen.');
      } else {
        setError(err.response?.data?.error || err.message || 'Fehler bei der Registrierung');
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Passkey "${name}" wirklich löschen?`)) return;
    setError('');
    try {
      await deletePasskey(id);
      setSuccess('Passkey gelöscht.');
      await loadCredentials();
    } catch {
      setError('Fehler beim Löschen des Passkeys.');
    }
  };

  if (!supported) {
    return (
      <Section title="Passkeys">
        <Banner tone="info">Dein Browser oder Gerät unterstützt keine Passkeys.</Banner>
      </Section>
    );
  }

  return (
    <Section
      title="Passkeys"
      description="Mit Passkeys meldest du dich schnell und sicher per Fingerabdruck, Gesichtserkennung oder Bildschirmsperre an."
    >
      <div className="flex flex-col gap-3">
        {error && <Banner tone="danger">{error}</Banner>}
        {success && <Banner tone="success">{success}</Banner>}

        {credentials.length > 0 && (
          <ul className="flex flex-col gap-2">
            {credentials.map((cred) => (
              <Card as="li" key={cred.id} padding="sm" className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-ink">{cred.deviceName}</p>
                  <p className="text-sm text-muted">
                    Erstellt: {new Date(cred.createdAt).toLocaleDateString('de-DE')}
                    {cred.lastUsedAt && ` — zuletzt: ${new Date(cred.lastUsedAt).toLocaleDateString('de-DE')}`}
                  </p>
                </div>
                <IconButton
                  label={`Passkey ${cred.deviceName} löschen`}
                  icon={<Trash2 className="h-4 w-4" />}
                  variant="ghost"
                  onClick={() => handleDelete(cred.id, cred.deviceName)}
                />
              </Card>
            ))}
          </ul>
        )}

        <Button type="button" onClick={handleRegister} disabled={isRegistering} loading={isRegistering} className="self-start">
          {isRegistering ? 'Registriere...' : 'Passkey hinzufügen'}
        </Button>
      </div>
    </Section>
  );
};
